import * as RadixPopover from "@radix-ui/react-popover";
import { Check } from "@phosphor-icons/react";
import { forwardRef, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn } from "@/lib/emotionTw";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding } from "@/components/ui/overlays/overlayPositioning";
import { Spinner } from "../../feedback/Spinner";

export type SuggestInputItem = {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
};

export type SuggestInputProps = {
  suggestions: SuggestInputItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onSuggestionSelect?: (item: SuggestInputItem) => void;
  renderItem?: (item: SuggestInputItem) => ReactNode;
  placeholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, emitted in the original class-list order. Verified against the
 * frozen baseline build, except text-entry focus boxes now render a single
 * active border without the old ring/outline layer. */

const siFieldBase: CSSObject = {
  height: "2.25rem",
  width: "100%",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "none",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&::placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  }
};

const siFieldError: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
  "&:focus-visible": {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  },
  '&[aria-expanded="true"]': {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  }
};

const siFieldDefaultBorder: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))",
  "&:focus-visible": {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  '&[aria-expanded="true"]': {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  }
};

const SuggestInputField = styled("input", {
  label: "AgenticSuggestInput-field",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { error: boolean } }>(({ ownerState }) => [
  siFieldBase,
  ownerState.error ? siFieldError : siFieldDefaultBorder
]);

const SuggestInputContent = styled(RadixPopover.Content, {
  label: "AgenticSuggestInput-content"
})({
  zIndex: 1000,
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  padding: ".25rem",
  "--tw-shadow": "var(--bfrs-shadow-elevated)",
  "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  ...kfContentIn,
  animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)",
  // searchableSelectContentCollisionClassName, inlined.
  maxHeight: "min(18rem,var(--radix-popover-content-available-height))",
  width: "var(--radix-popover-trigger-width)",
  overflow: "hidden"
});

// searchableSelectListboxCollisionClassName, inlined.
const SuggestInputListbox = styled("div", {
  label: "AgenticSuggestInput-listbox"
})({
  maxHeight: "min(14rem,max(4rem,calc(var(--radix-popover-content-available-height) - 3rem)))",
  overflow: "auto"
});

const SuggestInputLoading = styled("div", {
  label: "AgenticSuggestInput-loading"
})({
  display: "flex",
  height: "4rem",
  alignItems: "center",
  justifyContent: "center",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SuggestInputEmpty = styled("div", {
  label: "AgenticSuggestInput-empty"
})({
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingTop: "1rem",
  paddingBottom: "1rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

interface SuggestInputOptionOwnerState {
  selected: boolean;
  active: boolean;
}

const SuggestInputOption = styled("button", {
  label: "AgenticSuggestInput-option",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: SuggestInputOptionOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    width: "100%",
    alignItems: "center",
    gap: ".5rem",
    borderRadius: "var(--bfrs-radius-sm)",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    paddingTop: ".5rem",
    paddingBottom: ".5rem",
    textAlign: "left",
    fontSize: ".875rem",
    lineHeight: "1.25rem",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
    },
    "&:disabled": {
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
    }
  },
  ownerState.selected && { fontWeight: 700 },
  ownerState.active && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
  }
]);

const SuggestInputOptionBody = styled("span", {
  label: "AgenticSuggestInput-optionBody"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const SuggestInputCheckIcon = styled(Check, {
  label: "AgenticSuggestInput-checkIcon"
})({
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const SuggestItemLayout = styled("span", {
  label: "AgenticSuggestInput-itemLayout"
})({
  display: "grid",
  gap: ".125rem"
});

const SuggestItemLabel = styled("span", {
  label: "AgenticSuggestInput-itemLabel",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { selected: boolean } }>(({ ownerState }) => [
  {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  ownerState.selected ? { fontWeight: 700 } : { fontWeight: 500 }
]);

const SuggestItemDescription = styled("span", {
  label: "AgenticSuggestInput-itemDescription"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

export const SuggestInput = forwardRef<HTMLInputElement, SuggestInputProps>(
  ({ suggestions, value, defaultValue = "", onValueChange, onSuggestionSelect, renderItem, placeholder = "Search or type", emptyText = "No suggestions found", loading, disabled, error, className, id, "aria-describedby": describedBy, "aria-invalid": ariaInvalid }, ref) => {
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const currentValue = value ?? internalValue;
    const visibleSuggestions = useMemo(() => suggestions, [suggestions]);
    const showPanel = open && (loading || visibleSuggestions.length > 0 || currentValue.length > 0);

    const assignInputRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
    };

    const commitValue = (nextValue: string) => {
      if (value === undefined) setInternalValue(nextValue);
      onValueChange?.(nextValue);
    };

    const selectSuggestion = (item: SuggestInputItem) => {
      if (item.disabled) return;
      commitValue(item.value);
      onSuggestionSelect?.(item);
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setOpen(true);
        setActiveIndex((index) => Math.min(index + 1, Math.max(visibleSuggestions.length - 1, 0)));
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
      }
      if (event.key === "Escape") setOpen(false);
      if (event.key === "Enter" && showPanel && visibleSuggestions[activeIndex]) {
        event.preventDefault();
        selectSuggestion(visibleSuggestions[activeIndex]);
      }
    };

    return (
      <RadixPopover.Root open={showPanel} onOpenChange={setOpen}>
        <RadixPopover.Anchor asChild>
          <SuggestInputField
            ref={assignInputRef}
            id={id}
            value={currentValue}
            disabled={disabled}
            aria-autocomplete="list"
            aria-expanded={showPanel}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? error}
            placeholder={placeholder}
            ownerState={{ error: Boolean(error) }}
            className={className}
            onChange={(event) => {
              commitValue(event.currentTarget.value);
              setOpen(true);
              setActiveIndex(0);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
          />
        </RadixPopover.Anchor>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <SuggestInputContent
              align="start"
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <SuggestInputListbox role="listbox">
                {loading ? (
                  <SuggestInputLoading>
                    <Spinner size="sm" />
                  </SuggestInputLoading>
                ) : visibleSuggestions.length === 0 ? (
                  <SuggestInputEmpty>{emptyText}</SuggestInputEmpty>
                ) : (
                  visibleSuggestions.map((item, index) => {
                    const selected = item.value === currentValue;
                    return (
                      <SuggestInputOption
                        key={item.value}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        disabled={item.disabled}
                        ownerState={{ selected, active: activeIndex === index }}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => selectSuggestion(item)}
                      >
                        <SuggestInputOptionBody>{renderItem ? renderItem(item) : <DefaultSuggestItem item={item} selected={selected} />}</SuggestInputOptionBody>
                        {selected && <SuggestInputCheckIcon size={14} weight="bold" />}
                      </SuggestInputOption>
                    );
                  })
                )}
              </SuggestInputListbox>
            </SuggestInputContent>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  }
);

function DefaultSuggestItem({ item, selected }: { item: SuggestInputItem; selected: boolean }) {
  return (
    <SuggestItemLayout>
      <SuggestItemLabel ownerState={{ selected }}>{item.label}</SuggestItemLabel>
      {item.description && <SuggestItemDescription>{item.description}</SuggestItemDescription>}
    </SuggestItemLayout>
  );
}

SuggestInput.displayName = "SuggestInput";
